import type { ComponentProps } from 'react'
import { Icon, type IconName } from './Icon'

/**
 * A button.
 *
 * One component so that padding, focus and disabled behaviour cannot drift
 * between eighteen hand-written buttons, which is what they had done.
 *
 * An icon-only button still carries a name. `label` is always required and
 * becomes the accessible name and the tooltip even when it is not drawn, so a
 * control is never identified by a picture alone.
 */

export interface ButtonProps extends Omit<ComponentProps<'button'>, 'children'> {
  label: string
  icon?: IconName
  /** Draw the icon only. The label still names the control. */
  iconOnly?: boolean
  tone?: 'default' | 'primary' | 'quiet' | 'danger'
  size?: 'normal' | 'small'
}

/** The classes a Button carries, for a link that must look like one. */
// Beside Button so the two cannot drift; costs only a full reload when this file changes.
// oxlint-disable-next-line react/only-export-components
export function buttonClass({
  tone = 'default',
  size = 'normal',
  iconOnly,
}: Pick<ButtonProps, 'tone' | 'size' | 'iconOnly'> = {}): string {
  return [
    'btn',
    tone !== 'default' ? `btn-${tone}` : '',
    size === 'small' ? 'btn-sm' : '',
    iconOnly ? 'btn-icon' : '',
  ]
    .filter(Boolean)
    .join(' ')
}

export function Button({
  label,
  icon,
  iconOnly,
  tone = 'default',
  size = 'normal',
  className,
  title,
  ...rest
}: ButtonProps) {
  const classes = [buttonClass({ tone, size, iconOnly }), className].filter(Boolean).join(' ')

  return (
    <button
      type="button"
      className={classes}
      // Named even when only the icon is drawn.
      aria-label={iconOnly ? label : undefined}
      title={title ?? (iconOnly ? label : undefined)}
      {...rest}
    >
      {icon && <Icon name={icon} size={size === 'small' ? 14 : 16} />}
      {!iconOnly && <span>{label}</span>}
    </button>
  )
}
